import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import FolderTree from "./FolderTree";
import DocumentList from "./DocumentList";
import DocumentUpload from "./DocumentUpload";
import * as documentService from "../../../services/documentService";
import departmentService from "../../../services/departmentService";
import { usePermissions } from "../../../hooks/usePermissions";
import { DOCUMENT_PERMISSIONS } from "../../../utils/documentPermissions";

// ─── Constants ───

const SIDEBAR_WIDTH = 280;
const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_CHARS = 2;

// ─── DocumentsPage ───

const DocumentsPage = () => {
  const { hasPermission } = usePermissions();
  const authUser = useSelector((state) => state.auth?.user);

  // Determine if user is admin/superAdmin (needs department picker)
  const userRole = String(authUser?.role || "").toLowerCase();
  const isAdminRole = ["admin", "institute", "superadmin"].includes(userRole);

  // Permissions
  const canUpload = hasPermission(DOCUMENT_PERMISSIONS.UPLOAD);

  // ─── State ───

  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "uploadDate", sort: "desc" }]);
  const [filters, setFilters] = useState({});

  // Dialogs
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderName, setCreateFolderName] = useState("");
  const [createFolderParentId, setCreateFolderParentId] = useState(null);
  const [createFolderLoading, setCreateFolderLoading] = useState(false);

  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [renameFolderLoading, setRenameFolderLoading] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'document'|'folder', data }
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [moveDocumentOpen, setMoveDocumentOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState(null);
  const [moveLoading, setMoveLoading] = useState(false);

  const [editDocumentOpen, setEditDocumentOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", tags: [] });
  const [editLoading, setEditLoading] = useState(false);

  // Refs
  const searchTimerRef = useRef(null);

  // ─── Fetch Folder Tree ───

  const fetchFolderTree = useCallback(async () => {
    // For admin roles, require a department to be selected first
    if (isAdminRole && !selectedDepartmentId) {
      setFolders([]);
      return;
    }

    setFoldersLoading(true);
    try {
      const params = isAdminRole && selectedDepartmentId ? { departmentId: selectedDepartmentId } : {};
      const response = await documentService.getFolderTree(params);
      const data = response?.data?.data || response?.data || [];
      setFolders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[DocumentsPage] fetchFolderTree error:", error?.message || error);
      toast.error("Failed to load folders");
    } finally {
      setFoldersLoading(false);
    }
  }, [isAdminRole, selectedDepartmentId]);

  // ─── Fetch Documents ───

  const fetchDocuments = useCallback(
    async (folderId, page = 1, pageSize = 20, sort = sortModel, activeFilters = filters) => {
      if (!folderId) {
        setDocuments([]);
        setPagination({ page: 1, pageSize: 20, totalItems: 0, totalPages: 0 });
        return;
      }

      setLoading(true);
      setIsSearching(false);

      try {
        const sortField = sort?.[0]?.field || "uploadDate";
        const sortDir = sort?.[0]?.sort || "desc";

        const params = {
          folderId,
          page,
          pageSize,
          sortBy: sortField,
          sortDir,
          ...activeFilters,
        };

        // Admin/superAdmin pass departmentId as query param
        if (isAdminRole && selectedDepartmentId) {
          params.departmentId = selectedDepartmentId;
        }

        const response = await documentService.listDocuments(params);
        const data = response?.data?.data || response?.data || {};

        setDocuments(data.documents || data.rows || []);
        setPagination({
          page: data.page || page,
          pageSize: data.pageSize || pageSize,
          totalItems: data.totalItems || data.total || 0,
          totalPages: data.totalPages || 0,
        });
      } catch (error) {
        console.error("[DocumentsPage] fetchDocuments error:", error?.message || error);
        toast.error("Failed to load documents");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    },
    [sortModel, filters, isAdminRole, selectedDepartmentId]
  );

  // ─── Search Documents ───

  const performSearch = useCallback(
    async (query, page = 1, pageSize = 20) => {
      if (!query || query.length < SEARCH_MIN_CHARS) return;

      setLoading(true);
      setIsSearching(true);

      try {
        const params = { query, page, pageSize };

        // Admin/superAdmin pass departmentId as query param
        if (isAdminRole && selectedDepartmentId) {
          params.departmentId = selectedDepartmentId;
        }

        const response = await documentService.searchDocuments(params);
        const data = response?.data?.data || response?.data || {};

        setDocuments(data.documents || data.rows || []);
        setPagination({
          page: data.page || page,
          pageSize: data.pageSize || pageSize,
          totalItems: data.totalItems || data.total || 0,
          totalPages: data.totalPages || 0,
        });
      } catch (error) {
        console.error("[DocumentsPage] searchDocuments error:", error?.message || error);
        toast.error("Search failed");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    },
    [isAdminRole, selectedDepartmentId]
  );

  // ─── Effects ───

  // Fetch departments for admin/superAdmin
  useEffect(() => {
    if (!isAdminRole) return;

    const fetchDepartments = async () => {
      setDepartmentsLoading(true);
      try {
        const response = await departmentService.getDepartmentNames();
        const data = response?.data?.data || response?.data?.departments || response?.data || [];
        setDepartments(Array.isArray(data) ? data : []);
        // Auto-select first department if only one exists
        if (Array.isArray(data) && data.length === 1) {
          setSelectedDepartmentId(data[0].id);
        }
      } catch (error) {
        console.error("[DocumentsPage] fetchDepartments error:", error?.message || error);
        toast.error("Failed to load departments");
      } finally {
        setDepartmentsLoading(false);
      }
    };

    fetchDepartments();
  }, [isAdminRole]);

  // Fetch folder tree on mount (or when department changes for admin)
  useEffect(() => {
    fetchFolderTree();
  }, [fetchFolderTree]);

  // Reset folder selection when department changes
  useEffect(() => {
    setSelectedFolderId(null);
    setDocuments([]);
    setSearchQuery("");
    setIsSearching(false);
  }, [selectedDepartmentId]);

  // Fetch documents when folder changes (only if not actively searching)
  useEffect(() => {
    if (!isSearching && selectedFolderId) {
      fetchDocuments(selectedFolderId, 1, pagination.pageSize, sortModel, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId]);

  // Debounced search
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!searchQuery || searchQuery.length < SEARCH_MIN_CHARS) {
      // Clear search — go back to folder listing
      if (isSearching) {
        setIsSearching(false);
        if (selectedFolderId) {
          fetchDocuments(selectedFolderId, 1, pagination.pageSize, sortModel, filters);
        } else {
          setDocuments([]);
        }
      }
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ─── Event Handlers ───

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId);
    setSearchQuery("");
    setIsSearching(false);
  };

  const handlePageChange = (page, pageSize) => {
    if (isSearching) {
      performSearch(searchQuery, page, pageSize);
    } else {
      fetchDocuments(selectedFolderId, page, pageSize, sortModel, filters);
    }
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    if (!isSearching && selectedFolderId) {
      fetchDocuments(selectedFolderId, 1, pagination.pageSize, model, filters);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (!isSearching && selectedFolderId) {
      fetchDocuments(selectedFolderId, 1, pagination.pageSize, sortModel, newFilters);
    }
  };

  // ─── Document Actions ───

  const handleDocumentAction = async (action, document) => {
    switch (action) {
      case "download":
        handleDownload(document);
        break;
      case "edit":
        handleOpenEditDocument(document);
        break;
      case "move":
        handleOpenMoveDocument(document);
        break;
      case "delete":
        setDeleteTarget({ type: "document", data: document });
        setDeleteConfirmOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await documentService.downloadDocument(
        document.id || document.uuid,
        isAdminRole && selectedDepartmentId ? { departmentId: selectedDepartmentId } : {}
      );
      const url = response?.data?.data?.url || response?.data?.url;
      if (url) {
        window.open(url, "_blank");
      } else {
        toast.error("Could not get download URL");
      }
    } catch (error) {
      console.error("[DocumentsPage] download error:", error?.message || error);
      toast.error(error?.response?.data?.message || "Download failed");
    }
  };

  // ─── Edit Document ───

  const handleOpenEditDocument = (document) => {
    setEditTarget(document);
    setEditForm({
      title: document.title || "",
      description: document.description || "",
      tags: document.tags || [],
    });
    setEditDocumentOpen(true);
  };

  const handleEditDocument = async () => {
    if (!editTarget) return;
    setEditLoading(true);
    try {
      const payload = {};
      if (editForm.title && editForm.title !== editTarget.title) {
        payload.title = editForm.title;
      }
      if (editForm.description !== editTarget.description) {
        payload.description = editForm.description;
      }
      if (JSON.stringify(editForm.tags) !== JSON.stringify(editTarget.tags)) {
        payload.tags = editForm.tags;
      }

      if (Object.keys(payload).length === 0) {
        setEditDocumentOpen(false);
        return;
      }

      await documentService.updateDocument(
        editTarget.id || editTarget.uuid,
        payload,
        isAdminRole && selectedDepartmentId ? { departmentId: selectedDepartmentId } : {}
      );
      toast.success("Document updated");
      setEditDocumentOpen(false);
      refreshDocuments();
    } catch (error) {
      console.error("[DocumentsPage] editDocument error:", error?.message || error);
      toast.error(error?.response?.data?.message || "Failed to update document");
    } finally {
      setEditLoading(false);
    }
  };

  // ─── Move Document ───

  const handleOpenMoveDocument = (document) => {
    setMoveTarget(document);
    setMoveTargetFolderId(null);
    setMoveDocumentOpen(true);
  };

  // ─── Delete (document or folder) ───

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const deptParam = isAdminRole && selectedDepartmentId ? { departmentId: selectedDepartmentId } : {};
      if (deleteTarget.type === "document") {
        await documentService.deleteDocument(deleteTarget.data.id || deleteTarget.data.uuid, deptParam);
        toast.success("Document deleted");
        refreshDocuments();
      } else if (deleteTarget.type === "folder") {
        await documentService.deleteFolder(deleteTarget.data.id, deptParam);
        toast.success("Folder deleted");
        if (selectedFolderId === deleteTarget.data.id) {
          setSelectedFolderId(null);
          setDocuments([]);
        }
        fetchFolderTree();
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error("[DocumentsPage] delete error:", error?.message || error);
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── Folder Actions (from FolderTree context menu) ───

  const handleCreateSubFolder = (parentFolder) => {
    setCreateFolderParentId(parentFolder?.id || null);
    setCreateFolderName("");
    setCreateFolderOpen(true);
  };

  const handleCreateFolder = async () => {
    if (!createFolderName.trim()) return;
    setCreateFolderLoading(true);
    try {
      const payload = {
        name: createFolderName.trim(),
        parentId: createFolderParentId || undefined,
      };
      // Admin/superAdmin must pass departmentId explicitly
      if (isAdminRole && selectedDepartmentId) {
        payload.departmentId = selectedDepartmentId;
      }
      await documentService.createFolder(payload);
      toast.success("Folder created");
      setCreateFolderOpen(false);
      fetchFolderTree();
    } catch (error) {
      console.error("[DocumentsPage] createFolder error:", error?.message || error);
      toast.error(error?.response?.data?.message || "Failed to create folder");
    } finally {
      setCreateFolderLoading(false);
    }
  };

  const handleOpenRenameFolder = (folder) => {
    setRenameFolderTarget(folder);
    setRenameFolderName(folder.name || "");
    setRenameFolderOpen(true);
  };

  const handleRenameFolder = async () => {
    if (!renameFolderTarget || !renameFolderName.trim()) return;
    setRenameFolderLoading(true);
    try {
      const deptParam = isAdminRole && selectedDepartmentId ? { departmentId: selectedDepartmentId } : {};
      await documentService.renameFolder(
        renameFolderTarget.id,
        { name: renameFolderName.trim() },
        deptParam
      );
      toast.success("Folder renamed");
      setRenameFolderOpen(false);
      fetchFolderTree();
    } catch (error) {
      console.error("[DocumentsPage] renameFolder error:", error?.message || error);
      toast.error(error?.response?.data?.message || "Failed to rename folder");
    } finally {
      setRenameFolderLoading(false);
    }
  };

  const handleDeleteFolder = (folder) => {
    setDeleteTarget({ type: "folder", data: folder });
    setDeleteConfirmOpen(true);
  };

  const handleMoveFolder = async (folder) => {
    // For folder move, reuse the move dialog with folder context
    setMoveTarget({ ...folder, _isFolder: true });
    setMoveTargetFolderId(null);
    setMoveDocumentOpen(true);
  };

  const handleMoveFolderConfirm = async () => {
    if (!moveTarget || !moveTargetFolderId) return;
    setMoveLoading(true);
    try {
      const deptParam = isAdminRole && selectedDepartmentId ? { departmentId: selectedDepartmentId } : {};
      if (moveTarget._isFolder) {
        await documentService.moveFolder(
          moveTarget.id,
          { targetParentId: moveTargetFolderId },
          deptParam
        );
        toast.success("Folder moved");
        fetchFolderTree();
      } else {
        await documentService.moveDocument(
          moveTarget.id || moveTarget.uuid,
          { targetFolderId: moveTargetFolderId },
          deptParam
        );
        toast.success("Document moved");
        refreshDocuments();
      }
      setMoveDocumentOpen(false);
    } catch (error) {
      console.error("[DocumentsPage] move error:", error?.message || error);
      toast.error(error?.response?.data?.message || "Move failed");
    } finally {
      setMoveLoading(false);
    }
  };

  // ─── Upload Complete ───

  const handleUploadComplete = () => {
    refreshDocuments();
  };

  // ─── Refresh ───

  const refreshDocuments = () => {
    if (isSearching && searchQuery.length >= SEARCH_MIN_CHARS) {
      performSearch(searchQuery, pagination.page, pagination.pageSize);
    } else if (selectedFolderId) {
      fetchDocuments(selectedFolderId, pagination.page, pagination.pageSize, sortModel, filters);
    }
  };

  // ─── Toolbar "New Folder" (root level) ───

  const handleNewFolderToolbar = () => {
    setCreateFolderParentId(selectedFolderId || null);
    setCreateFolderName("");
    setCreateFolderOpen(true);
  };

  // ─── Render ───

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", gap: 2, p: 2 }}>
      {/* Department Selector (admin/superAdmin only) */}
      {isAdminRole && (
        <Paper elevation={1} sx={{ p: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Department</InputLabel>
            <Select
              value={selectedDepartmentId || ""}
              label="Select Department"
              onChange={(e) => setSelectedDepartmentId(e.target.value || null)}
              disabled={departmentsLoading}
            >
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {departmentsLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Paper>
      )}

      <Box sx={{ display: "flex", flex: 1, gap: 2, overflow: "hidden" }}>
      {/* Sidebar — Folder Tree */}
      <Paper
        elevation={1}
        sx={{
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Folders
          </Typography>
        </Box>
        {foldersLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <FolderTree
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={handleSelectFolder}
            onCreateSubFolder={handleCreateSubFolder}
            onRenameFolder={handleOpenRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveFolder={handleMoveFolder}
          />
        )}
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Toolbar */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search documents (min 2 chars)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250, flex: 1, maxWidth: 400 }}
            />

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* New Folder Button */}
            {canUpload && (
              <Button
                variant="outlined"
                startIcon={<CreateNewFolderIcon />}
                onClick={handleNewFolderToolbar}
                size="small"
                disabled={isAdminRole && !selectedDepartmentId}
              >
                New Folder
              </Button>
            )}

            {/* Upload Button */}
            {canUpload && (
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadOpen(true)}
                disabled={!selectedFolderId || (isAdminRole && !selectedDepartmentId)}
                size="small"
              >
                Upload
              </Button>
            )}
          </Box>
        </Paper>

        {/* Document List */}
        <Paper elevation={1} sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {!selectedFolderId && !isSearching ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                py: 8,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a folder
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a folder from the sidebar to browse documents, or use search to find files across the department.
              </Typography>
            </Box>
          ) : (
            <DocumentList
              documents={documents}
              pagination={pagination}
              loading={loading}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onPageChange={handlePageChange}
              onFilterChange={handleFilterChange}
              onDocumentAction={handleDocumentAction}
            />
          )}
        </Paper>
      </Box>
      </Box>

      {/* ─── Dialogs ─── */}

      {/* Upload Dialog */}
      <DocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        folderId={selectedFolderId}
        departmentId={isAdminRole ? selectedDepartmentId : undefined}
        onUploadComplete={handleUploadComplete}
      />

      {/* Create Folder Dialog */}
      <Dialog
        open={createFolderOpen}
        onClose={() => !createFolderLoading && setCreateFolderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={createFolderName}
            onChange={(e) => setCreateFolderName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && createFolderName.trim()) {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)} disabled={createFolderLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFolder}
            disabled={!createFolderName.trim() || createFolderLoading}
          >
            {createFolderLoading ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog
        open={renameFolderOpen}
        onClose={() => !renameFolderLoading && setRenameFolderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && renameFolderName.trim()) {
                handleRenameFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFolderOpen(false)} disabled={renameFolderLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRenameFolder}
            disabled={!renameFolderName.trim() || renameFolderLoading}
          >
            {renameFolderLoading ? "Renaming..." : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleteLoading && setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.type === "folder"
              ? `Are you sure you want to delete the folder "${deleteTarget?.data?.name}"? The folder must be empty.`
              : `Are you sure you want to delete "${deleteTarget?.data?.title || deleteTarget?.data?.fileName}"? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Document/Folder Dialog */}
      <Dialog
        open={moveDocumentOpen}
        onClose={() => !moveLoading && setMoveDocumentOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Move {moveTarget?._isFolder ? "Folder" : "Document"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the destination folder for "{moveTarget?.name || moveTarget?.title || moveTarget?.fileName}".
          </DialogContentText>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            <FolderTree
              folders={folders}
              selectedFolderId={moveTargetFolderId}
              onSelectFolder={setMoveTargetFolderId}
              onCreateSubFolder={() => {}}
              onRenameFolder={() => {}}
              onDeleteFolder={() => {}}
              onMoveFolder={() => {}}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDocumentOpen(false)} disabled={moveLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMoveFolderConfirm}
            disabled={!moveTargetFolderId || moveLoading}
          >
            {moveLoading ? "Moving..." : "Move"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog
        open={editDocumentOpen}
        onClose={() => !editLoading && setEditDocumentOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={editForm.title}
            onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
            inputProps={{ maxLength: 200 }}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={editForm.description}
            onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{ maxLength: 1000 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Tags (comma-separated)"
            value={editForm.tags.join(", ")}
            onChange={(e) => {
              const tags = e.target.value
                .split(",")
                .map((t) => t.trim())
                .filter((t) => t.length > 0)
                .slice(0, 5);
              setEditForm((prev) => ({ ...prev, tags }));
            }}
            helperText="Up to 5 tags, max 50 chars each"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDocumentOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditDocument}
            disabled={!editForm.title.trim() || editLoading}
          >
            {editLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsPage;
